'use client';

import { ShoppingListsDetailQuery } from '@fdc/__generated__/graphql';
import { CoreButton } from '@fdc/components/ui/core';
import { useSaveShoppingList } from '@fdc/hooks/nz/mutations/useSaveShoppingList';
import { useAppRouter } from '@fdc/hooks/useAppRouter';
import { CoreContentDialog } from '@woolworthslimited/core-components-react/modal';
import { snackBarService } from '@woolworthslimited/core-components-react/snack-bar';
import { CoreTextField } from '@woolworthslimited/core-components-react/text-field';
import { Dispatch, SetStateAction, useState } from 'react';

import {
  createDuplicateShoppingListPayload,
  createShoppingListPayload,
} from '../shopping-list-util';

import styles from './shopping-list-name.module.scss';

const LIST_NAME_MAX_LENGTH = 40;

export type CreatedShoppingList = {
  id: string;
  name: string;
  referenceId: string;
};

export type ShoppingListNameDialogProps = {
  type: 'create' | 'rename' | 'duplicate';
  open: boolean;
  dialogTitle: string;
  runDefaultSuccess?: boolean;
  existingList?: ShoppingListsDetailQuery['list'];
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
  successCallback?: (createdList?: CreatedShoppingList) => void;
  confirmButtonLabel?: string;
  onBack?: () => void;
  placeholderText?: string;
};

export const ShoppingListNameDialog: React.FC<ShoppingListNameDialogProps> = ({
  type,
  open,
  dialogTitle,
  existingList,
  runDefaultSuccess = true,
  setDialogOpen,
  successCallback,
  confirmButtonLabel,
  onBack,
  placeholderText,
}) => {
  const router = useAppRouter();
  const [newListName, setNewListName] = useState('');
  const [validListName, setValidListName] = useState(true);
  const [showError, setShowError] = useState(false);
  const { handleSaveShoppingList, loading: savingList } = useSaveShoppingList();

  const onDialogClose = () => {
    setNewListName('');
    setValidListName(true);
    setShowError(false);
    setDialogOpen(false);
  };

  const onBackHandler = () => {
    setNewListName('');
    setValidListName(true);
    setShowError(false);
    onBack?.();
  };

  const onInputValueChanged = (value: string) => {
    const cappedValue = value.slice(0, LIST_NAME_MAX_LENGTH);
    // Allows all printable ASCII characters (letters, numbers, spaces, and
    // standard punctuation/symbols).
    const valid = cappedValue === '' || /^[\x20-\x7E]+$/.test(cappedValue);
    setValidListName(valid);
    setNewListName(cappedValue);
  };

  const saveClickHandler = () => {
    const trimmedName = newListName.trim();
    let payload;

    switch (type) {
      case 'duplicate':
        payload = createDuplicateShoppingListPayload(existingList, trimmedName);
        break;
      case 'create':
        payload = createShoppingListPayload(trimmedName);
        break;
      case 'rename':
        payload = createShoppingListPayload(
          trimmedName,
          existingList.referenceId
        );
        break;
    }

    handleSaveShoppingList(payload)
      .then((res) => {
        const saveList = res.data?.saveList;
        if (
          ['CREATED', 'UPDATED'].includes(saveList?.action) &&
          saveList?.result === 'SUCCESS'
        ) {
          onDialogClose();

          if (runDefaultSuccess) {
            if (type === 'create' || type === 'duplicate') {
              if (type === 'duplicate') {
                snackBarService.show(`List duplicated.`);
              }
              router.push(`/lists/${saveList.id}`);
            }

            if (type === 'rename') {
              snackBarService.show(`List renamed to '${trimmedName}'.`);
            }
          }

          successCallback?.({
            id: saveList.id,
            name: saveList.name || trimmedName,
            referenceId: saveList.referenceId,
          });
        } else {
          setShowError(true);
        }
      })
      .catch(() => {
        setShowError(true);
      });
  };

  return (
    <CoreContentDialog
      isOpen={open}
      onClose={onDialogClose}
      size="small"
      titleProps={{ title: dialogTitle }}
      customFooter={
        <div className={styles.dialogFooter}>
          {showError && (
            <div className={styles.errorMessage}>
              {`We're having issue ${type === 'rename' ? 'renaming' : type === 'duplicate' ? 'duplicating' : 'creating'} your list. Please try again.`}
            </div>
          )}
          <div className={styles.buttonGroup}>
            {onBack && (
              <CoreButton
                label="Back"
                variant="secondary"
                icon={{ name: 'icon-navigation-chevron-left' }}
                clickHandler={onBackHandler}
              />
            )}
            <CoreButton
              label={confirmButtonLabel ?? 'Save'}
              isDisabled={
                newListName.trim() === '' || !validListName || savingList
              }
              clickHandler={saveClickHandler}
            />
          </div>
        </div>
      }
    >
      <div className={styles.newListName}>
        <CoreTextField
          id="new-list-name"
          label="Enter list name"
          value={newListName}
          placeholderText={placeholderText ?? 'Monthly shopping list'}
          maxLength={LIST_NAME_MAX_LENGTH}
          type="text"
          errorText={
            validListName
              ? ''
              : 'List name can only contain letters, numbers, punctuations, and spaces.'
          }
          onValueChanged={onInputValueChanged}
          isHidden={false}
        />
      </div>
    </CoreContentDialog>
  );
};
